/**
 * Ask the operator to confirm moving open claims during a member save
 * (WPS-160). `confirmClaimMoves` opens `MemberClaimMovesDialog` and resolves
 * `true` (save and move), `false` (save, leave the claims) or `null` (cancel,
 * save nothing), so an async save handler can simply await it. Render
 * `claimMovesDialog` once next to the form.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react'

import type { ClaimMovePreview } from '@/admin/hooks/use-membership-attributes'
import { MemberClaimMovesDialog } from '@/admin/pages/settings/member-claim-moves-dialog'

type Choice = boolean | null

export function useClaimMovesConfirm(): {
  confirmClaimMoves: (preview: ClaimMovePreview, memberName: string) => Promise<Choice>
  claimMovesDialog: ReactNode
} {
  const [pending, setPending] = useState<{
    preview: ClaimMovePreview
    memberName: string
  } | null>(null)
  const [moveClaims, setMoveClaims] = useState(true)
  const resolveRef = useRef<((choice: Choice) => void) | null>(null)

  const settle = useCallback((choice: Choice) => {
    resolveRef.current?.(choice)
    resolveRef.current = null
    setPending(null)
  }, [])

  const confirmClaimMoves = useCallback(
    (preview: ClaimMovePreview, memberName: string) =>
      new Promise<Choice>((resolve) => {
        // A second request supersedes an unanswered one.
        resolveRef.current?.(null)
        resolveRef.current = resolve
        setMoveClaims(true)
        setPending({ preview, memberName })
      }),
    [],
  )

  const claimMovesDialog = (
    <MemberClaimMovesDialog
      open={pending !== null}
      memberName={pending?.memberName ?? ''}
      preview={pending?.preview ?? null}
      moveClaims={moveClaims}
      onMoveClaimsChange={setMoveClaims}
      onCancel={() => settle(null)}
      onConfirm={() => settle(moveClaims)}
    />
  )

  return { confirmClaimMoves, claimMovesDialog }
}
